import React, { useEffect, useRef } from 'react'

const PartnersMarquee = () => {
  const marqueeRef = useRef(null)

  const partners = [
    { name: 'Helio', logo: '/partners/helio.png' },
    { name: '', logo: '/partners/aleh.png' },
    { name: 'Ethereum', logo: '/partners/Ethereum.png' },
    { name: 'Bitcoin', logo: '/partners/bitcoin.png' },
    { name: 'Shopify', logo: '/partners/Shopify.png' },
  ]

  useEffect(() => {
    const styleSheet = document.createElement('style')
    styleSheet.textContent = `
      @keyframes marqueeLeft {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }

      @keyframes movingBorder {
        0% {
          background-position: 0% 50%;
        }
        100% {
          background-position: 300% 50%;
        }
      }

      .marquee-container {
        mask: linear-gradient(90deg, 
          transparent 0%, 
          white 10%, 
          white 90%, 
          transparent 100%
        );
        -webkit-mask: linear-gradient(90deg, 
          transparent 0%, 
          white 10%, 
          white 90%, 
          transparent 100%
        );
      }
    `
    document.head.appendChild(styleSheet)
    return () => document.head.removeChild(styleSheet)
  }, [])

  const styles = {
    marqueeContainer: {
      width: '80%',
      overflow: 'hidden',
      position: 'relative',
      padding: '60px 0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },

    marqueeWrapper: {
      width: '100%',
      overflow: 'hidden',
      position: 'relative',
    },

    marquee: {
      display: 'flex',
      alignItems: 'center',
      gap: '50px',
      width: 'fit-content',
      animation: 'marqueeLeft 40s linear infinite',
    },

    marqueeContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '50px',
      flexShrink: 0,
    },

    partnerItem: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      minWidth: '220px',
      height: '75px',
      padding: '10px 15px',
      borderRadius: '20px',

      // Glassmorphism + vibrant gradient background
      background: `
    linear-gradient(135deg, rgba(79, 172, 254, 0.25), rgba(0, 242, 254, 0.15))
  `,

      backdropFilter: 'blur(16px) saturate(180%)',
      WebkitBackdropFilter: 'blur(16px) saturate(180%)',

      border: '1px solid rgba(255, 255, 255, 0.2)',

      boxShadow: `
    0 8px 32px 0 rgba(31, 38, 135, 0.37),
    0 4px 16px 0 rgba(0, 0, 0, 0.1), 
    inset 0 1px 0 rgba(255, 255, 255, 0.2)
  `,

      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      flexShrink: 0,
      zIndex: 1,
    },

    // Frosted glass overlay - remove all extra overlays
    animatedBorder: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '20px',
      padding: '2px',
      backgroundImage:
        'linear-gradient(90deg, transparent 0%, transparent 70%, #00A3FF 75%, #00E58F 80%, #00A3FF 85%, transparent 90%, transparent 100%)',
      backgroundSize: '300% 100%',
      backgroundPosition: '0% 50%',
      // animation: 'movingBorder 3.5s linear infinite',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
      WebkitMask:
        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      pointerEvents: 'none',
      filter:
        'blur(0.5px) drop-shadow(0 0 8px rgba(0, 163, 255, 0.8)) drop-shadow(0 0 16px rgba(0, 229, 143, 0.6)) drop-shadow(0 0 24px rgba(0, 163, 255, 0.4))',
    },

    partnerLogo: {
      width: '60px',
      height: '60px',
      objectFit: 'contain',
      filter: 'brightness(1.2) contrast(1.2)',
      transition: 'all 0.3s ease',
      zIndex: 2,
      position: 'relative',
    },

    partnerLogoHover: {
      transform: 'scale(1.1)',
    },

    partnerName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#ffffff',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
      whiteSpace: 'nowrap',
      zIndex: 2,
      position: 'relative',
      transition: 'all 0.3s ease',
    },

    partnerNameHover: {
      color: 'rgba(255, 255, 255, 0.8)',
      transform: 'translateY(-1px)',
    },
  }

  const PartnerCard = ({ partner, index }) => {
    const [isHovered, setIsHovered] = React.useState(false)

    return (
      <div
        key={index}
        style={{
          ...styles.partnerItem,
          ...(isHovered ? styles.partnerItemHover : {}),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Animated border */}
        <div style={styles.animatedBorder}></div>

        <img
          src={partner.logo}
          alt={partner.name}
          style={{
            ...styles.partnerLogo,
            ...(isHovered ? styles.partnerLogoHover : {}),
          }}
          onError={(e) => {
            e.target.style.display = 'none'
          }}
        />
        <span
          style={{
            ...styles.partnerName,
            ...(isHovered ? styles.partnerNameHover : {}),
          }}
        >
          {partner.name}
        </span>
      </div>
    )
  }

  // Create enough duplicates for seamless infinite scroll
  const renderPartnerSets = () => {
    const allPartners = []
    // Create enough duplicates to ensure seamless looping
    for (let setIndex = 0; setIndex < 4; setIndex++) {
      partners.forEach((partner, index) => {
        allPartners.push(
          <PartnerCard
            key={`set-${setIndex}-partner-${index}`}
            partner={partner}
            index={`set-${setIndex}-partner-${index}`}
          />
        )
      })
    }
    return allPartners
  }

  return (
    <div style={styles.marqueeContainer} className="marquee-container">
      <div style={styles.marqueeWrapper}>
        <div
          ref={marqueeRef}
          style={styles.marquee}
          onMouseEnter={(e) =>
            (e.currentTarget.style.animationPlayState = 'paused')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.animationPlayState = 'running')
          }
        >
          {renderPartnerSets()}
        </div>
      </div>
    </div>
  )
}

export default PartnersMarquee
